import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { TerminologyProvider } from '@/context/TerminologyContext';
import ErrorBoundary from '@/components/shared/ErrorBoundary';

// Lazy-loaded pages
const ResidentLogin = lazy(() => import('@/pages/public/ResidentLogin'));
const ResidentPortal = lazy(() => import('@/pages/resident/ResidentPortal'));
const ResidentWizard = lazy(() => import('@/pages/resident/ResidentWizard'));
const AdminLogin = lazy(() => import('@/pages/auth/AdminLogin'));
const ForgotPassword = lazy(() => import('@/pages/auth/ForgotPassword'));
const ResetPassword = lazy(() => import('@/pages/auth/ResetPassword'));
const AdminShell = lazy(() => import('@/components/layout/AdminShell'));
const PlatformShell = lazy(() => import('@/components/layout/PlatformShell'));
const CommandCenter = lazy(() => import('@/pages/admin/CommandCenter'));
const SetupStudio = lazy(() => import('@/pages/admin/SetupStudio'));
const Places = lazy(() => import('@/pages/admin/Places'));
const Circles = lazy(() => import('@/pages/admin/Circles'));
const Events = lazy(() => import('@/pages/admin/Events'));
const GreenHaritha = lazy(() => import('@/pages/admin/GreenHaritha'));
const GreenMetrics = lazy(() => import('@/pages/admin/GreenMetrics'));
const MediaLibrary = lazy(() => import('@/pages/admin/MediaLibrary'));
const ResidentAccess = lazy(() => import('@/pages/admin/ResidentAccess'));
const Properties = lazy(() => import('@/pages/admin/Properties'));
const Households = lazy(() => import('@/pages/admin/Households'));
const People = lazy(() => import('@/pages/admin/People'));
const UAT = lazy(() => import('@/pages/admin/UAT'));
const Audit = lazy(() => import('@/pages/admin/Audit'));
const DataVerification = lazy(() => import('@/pages/admin/DataVerification'));
const CommunityMaster = lazy(() => import('@/pages/admin/CommunityMaster'));
const ImportWizard = lazy(() => import('@/pages/admin/ImportWizard'));
const AdminSettings = lazy(() => import('@/pages/admin/Settings'));
const TenantList = lazy(() => import('@/pages/platform/TenantList'));
const TenantRecord = lazy(() => import('@/pages/platform/TenantRecord'));
const PlatformAudit = lazy(() => import('@/pages/platform/PlatformAudit'));
const NotFound = lazy(() => import('@/pages/NotFound'));

// ARGOV Sentra — Member Portal
const SentraShell = lazy(() => import('@/pages/sentra/SentraShell'));
const SentraDashboard = lazy(() => import('@/pages/sentra/SentraDashboard'));
const SentraMySpace = lazy(() => import('@/pages/sentra/SentraMySpace'));
const SentraRequests = lazy(() => import('@/pages/sentra/SentraRequests'));
const SentraCommunity = lazy(() => import('@/pages/sentra/SentraCommunity'));
const SentraPublicHome = lazy(() => import('@/pages/sentra/SentraPublicHome'));
const SentraLogin = lazy(() => import('@/pages/sentra/SentraLogin'));
const SentraLanding = lazy(() => import('@/pages/sentra/SentraLanding'));
// SentraRegister removed — /sentra/register redirects to /sentra/onboard
const SubscriptionPage = lazy(() => import('@/pages/admin/SubscriptionPage'));
const BillingPage = lazy(() => import('@/pages/admin/BillingPage'));
const CommunicationsPage = lazy(() => import('@/pages/admin/CommunicationsPage'));
const PlatformSubscriptionsPage = lazy(() => import('@/pages/platform/PlatformSubscriptionsPage'));
const SentraOnboard = lazy(() => import('@/pages/sentra/SentraOnboard'));
const SentraFindCommunity = lazy(() => import('@/pages/sentra/SentraFindCommunity'));
const SentraJoin = lazy(() => import('@/pages/sentra/SentraJoin'));
const SentraJoinStatus = lazy(() => import('@/pages/sentra/SentraJoinStatus'));
const MembershipRequests = lazy(() => import('@/pages/admin/MembershipRequests'));

const Spinner = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F3F6F9]">
    <div className="w-8 h-8 border-4 border-[#0176D3] border-t-transparent rounded-full animate-spin" />
  </div>
);

// Protected route for admin (community_admin, community_staff, platform_admin)
const ProtectedAdminRoute = ({ children }) => {
  const { user, isAdmin } = useAuth();
  if (user === null) return <Spinner />;
  if (!user || !isAdmin) return <Navigate to="/admin/login" replace />;
  return children;
};

// Protected route for platform admin only
const ProtectedPlatformRoute = ({ children }) => {
  const { user, isPlatformAdmin } = useAuth();
  if (user === null) return <Spinner />;
  if (!user || !isPlatformAdmin) return <Navigate to="/admin/login" replace />;
  return children;
};

// Protected route for residents (legacy /resident/portal and /resident/wizard)
const ProtectedResidentRoute = ({ children }) => {
  const { residentUser, residentLoading } = useAuth();
  if (residentLoading) return <Spinner />;
  if (!residentUser) return <Navigate to="/sentra/login" replace />;
  return children;
};

// Protected route for ARGOV Sentra — preserves destination for post-login redirect
const ProtectedSentraRoute = ({ children }) => {
  const { residentUser, residentLoading } = useAuth();
  const location = useLocation();
  if (residentLoading) return <Spinner />;
  if (!residentUser) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/sentra/login?next=${next}`} replace />;
  }
  return children;
};

// Redirect if already logged in
const AuthRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Spinner />;
  if (user) {
    if (user.role === 'platform_admin') return <Navigate to="/platform/tenants" replace />;
    return <Navigate to="/admin/dashboard" replace />;
  }
  return children;
};

const AdminWrapped = ({ children }) => (
  <ProtectedAdminRoute>
    <AdminShell>{children}</AdminShell>
  </ProtectedAdminRoute>
);

const PlatformWrapped = ({ children }) => (
  <ProtectedPlatformRoute>
    <PlatformShell>{children}</PlatformShell>
  </ProtectedPlatformRoute>
);

function AppRoutes() {
  return (
    <Suspense fallback={<Spinner />}>
      <Routes>
        {/* Root — generic Sentra landing (tenant-agnostic; no customer slug assumed) */}
        <Route path="/" element={<SentraLanding />} />
        {/* Sentra: AOS Onboarding (canonical) */}
        <Route path="/sentra/onboard" element={<SentraOnboard />} />
        {/* Sentra: /sentra/register → compatibility redirect */}
        <Route path="/sentra/register" element={<Navigate to="/sentra/onboard" replace />} />
        {/* Admin: Subscription + Entitlement */}
        <Route path="/admin/subscription" element={<SubscriptionPage />} />
        <Route path="/admin/billing" element={<BillingPage />} />
        <Route path="/admin/communications" element={<CommunicationsPage />} />
        {/* Platform: Subscription management */}
        <Route path="/platform/subscriptions" element={<PlatformSubscriptionsPage />} />
        {/* Sentra: Find Community + Join flow (Phase B) */}
        <Route path="/sentra/find-community" element={<SentraFindCommunity />} />
        <Route path="/sentra/join/:slug" element={<SentraJoin />} />
        <Route path="/sentra/join-request/status" element={<SentraJoinStatus />} />
        {/* /resident/login — backward-compat redirect to ARGOV Sentra */}
        <Route path="/resident/login" element={<Navigate to="/sentra/login" replace />} />
        <Route path="/resident/portal" element={<ProtectedResidentRoute><ResidentPortal /></ProtectedResidentRoute>} />
        <Route path="/resident/wizard" element={<ProtectedResidentRoute><ResidentWizard /></ProtectedResidentRoute>} />

        {/* Admin Auth */}
        <Route path="/admin/login" element={<AuthRoute><AdminLogin /></AuthRoute>} />
        <Route path="/admin/forgot-password" element={<ForgotPassword />} />
        <Route path="/admin/reset-password" element={<ResetPassword />} />

        {/* Community Admin */}
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="/admin/dashboard" element={<AdminWrapped><CommandCenter /></AdminWrapped>} />
        <Route path="/admin/setup" element={<AdminWrapped><SetupStudio /></AdminWrapped>} />
        <Route path="/admin/places" element={<AdminWrapped><Places /></AdminWrapped>} />
        <Route path="/admin/circles" element={<AdminWrapped><Circles /></AdminWrapped>} />
        <Route path="/admin/events" element={<AdminWrapped><Events /></AdminWrapped>} />
        <Route path="/admin/green" element={<AdminWrapped><GreenHaritha /></AdminWrapped>} />
        <Route path="/admin/green-metrics" element={<AdminWrapped><GreenMetrics /></AdminWrapped>} />
        <Route path="/admin/media" element={<AdminWrapped><MediaLibrary /></AdminWrapped>} />
        <Route path="/admin/residents" element={<AdminWrapped><ResidentAccess /></AdminWrapped>} />
        {/* B4 Registry */}
        <Route path="/admin/properties" element={<AdminWrapped><Properties /></AdminWrapped>} />
        <Route path="/admin/households" element={<AdminWrapped><Households /></AdminWrapped>} />
        <Route path="/admin/persons" element={<AdminWrapped><People /></AdminWrapped>} />
        <Route path="/admin/people" element={<Navigate to="/admin/persons" replace />} />
        <Route path="/admin/uat" element={<AdminWrapped><UAT /></AdminWrapped>} />
        <Route path="/admin/audit" element={<AdminWrapped><Audit /></AdminWrapped>} />
        <Route path="/admin/change-requests" element={<AdminWrapped><DataVerification /></AdminWrapped>} />
        <Route path="/admin/community-master" element={<AdminWrapped><CommunityMaster /></AdminWrapped>} />
        <Route path="/admin/import" element={<AdminWrapped><ImportWizard /></AdminWrapped>} />
        <Route path="/admin/settings" element={<AdminWrapped><AdminSettings /></AdminWrapped>} />
        <Route path="/admin/membership-requests" element={<AdminWrapped><MembershipRequests /></AdminWrapped>} />

        {/* Platform Admin */}
        <Route path="/platform" element={<Navigate to="/platform/tenants" replace />} />
        <Route path="/platform/tenants" element={<PlatformWrapped><TenantList /></PlatformWrapped>} />
        <Route path="/platform/tenants/:tenantId" element={<PlatformWrapped><TenantRecord /></PlatformWrapped>} />
        <Route path="/platform/audit" element={<PlatformWrapped><PlatformAudit /></PlatformWrapped>} />

        {/* ARGOV Sentra — Public (no auth required) */}
        <Route path="/s/:slug" element={<SentraPublicHome />} />
        <Route path="/s/:slug/login" element={<SentraLogin />} />
        <Route path="/sentra/login" element={<SentraLogin />} />

        {/* ARGOV Sentra — Protected member portal (authenticated residents) */}
        <Route path="/sentra" element={<ProtectedSentraRoute><SentraShell /></ProtectedSentraRoute>}>
          <Route index element={<Navigate to="dashboard" replace />} />
          <Route path="dashboard" element={<SentraDashboard />} />
          <Route path="space" element={<SentraMySpace />} />
          <Route path="requests" element={<SentraRequests />} />
          <Route path="community" element={<SentraCommunity />} />
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <TerminologyProvider>
          <BrowserRouter>
            <AppRoutes />
            <Toaster position="top-right" richColors closeButton />
          </BrowserRouter>
        </TerminologyProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
